import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Form, InputGroup, ListGroup, Badge, Spinner, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faBuilding,
  faCodeBranch,
  faIndustry,
  faMapMarkerAlt,
  faCheckCircle,
  faFilter,
  faTimes
} from '@fortawesome/free-solid-svg-icons';

const EntityPicker = ({
  selectedFilters,
  dealers,
  branches,
  companies,
  sites,
  totalCounts,
  isSearching = false,
  onFilterChange,
  onSearch,
  onCancelSearch,
  onClearAll,
  loading = false,
  error = null,
  onAddSelection,
  accumulatedSelections = [],
  onRemoveSelection,
  onClearAllSelections
}) => {
  const [localSearchTerms, setLocalSearchTerms] = useState({
    dealer: '',
    branch: '',
    company: '',
    site: ''
  });
  const [focusedLevel, setFocusedLevel] = useState(null);
  const [filteredOptions, setFilteredOptions] = useState({});
  
  const searchRefs = {
    dealer: useRef(null),
    branch: useRef(null),
    company: useRef(null),
    site: useRef(null)
  };
  
  const blurTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  const levelConfig = {
    dealer: {
      icon: faBuilding,
      color: 'primary',
      label: 'Dealer',
      placeholder: 'Search dealers...'
    },
    branch: {
      icon: faCodeBranch,
      color: 'info',
      label: 'Branch',
      placeholder: 'Search branches...'
    },
    company: {
      icon: faIndustry,
      color: 'success',
      label: 'Company',
      placeholder: 'Type to search companies...'
    },
    site: {
      icon: faMapMarkerAlt,
      color: 'warning',
      label: 'Site',
      placeholder: 'Type to search sites...'
    }
  };

  const allOptions = {
    dealer: dealers,
    branch: branches,
    company: companies,
    site: sites
  };

  // Filter options locally based on search term
  const filterOptions = useCallback((level, searchTerm) => {
    const options = allOptions[level] || [];
    
    if (!searchTerm.trim()) {
      return options.slice(0, 100); // Show first 100
    }
    
    const searchLower = searchTerm.toLowerCase();
    return options.filter(option =>
      option.label.toLowerCase().includes(searchLower) ||
      (option.name && option.name.toLowerCase().includes(searchLower)) ||
      (option.code && option.code.toLowerCase().includes(searchLower))
    ).slice(0, 100);
  }, [dealers, branches, companies, sites]);

  // Handle input change
  const handleInputChange = useCallback((level, value) => {
    setLocalSearchTerms(prev => ({
      ...prev,
      [level]: value
    }));

    // For dealer/branch: filter locally immediately
    if (level === 'dealer' || level === 'branch') {
      const filtered = filterOptions(level, value);
      setFilteredOptions(prev => ({
        ...prev,
        [level]: filtered
      }));
    }
    
    // For company/site: just update the input, don't filter yet
    // User needs to press Enter or click search
  }, [filterOptions]);

  // Handle search trigger (Enter or search icon)
  const handleSearchTrigger = useCallback((level) => {
    if (level === 'company' || level === 'site') {
      const searchTerm = localSearchTerms[level];
      
      // If there's already data loaded (dealer selected), do local search
      if ((level === 'company' && companies.length > 0) || (level === 'site' && sites.length > 0)) {
        // Local typeahead search
        const filtered = filterOptions(level, searchTerm);
        setFilteredOptions(prev => ({
          ...prev,
          [level]: filtered
        }));
        setFocusedLevel(level);
      } else {
        // No data loaded yet, trigger API search
        if (onSearch) {
          onSearch(level, searchTerm);
        }
        setFocusedLevel(level);
      }
    }
  }, [localSearchTerms, companies, sites, filterOptions, onSearch]);

  // Handle focus
  const handleFocus = useCallback((level) => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    
    setFocusedLevel(level);
    
    // Show filtered options based on current search term
    const filtered = filterOptions(level, localSearchTerms[level]);
    setFilteredOptions(prev => ({
      ...prev,
      [level]: filtered
    }));
  }, [filterOptions, localSearchTerms]);

  // Handle blur
  const handleBlur = useCallback((level) => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }
    
    blurTimeoutRef.current = setTimeout(() => {
      setFocusedLevel(prevFocused => {
        if (prevFocused === level) {
          setFilteredOptions(prev => ({
            ...prev,
            [level]: []
          }));
          return null;
        }
        return prevFocused;
      });
      blurTimeoutRef.current = null;
    }, 300);
  }, []);

  // Handle option selection
  const handleOptionSelect = useCallback((level, option) => {
    onFilterChange(level, option.value);
    
    // Update local search term to show selected label
    setLocalSearchTerms(prev => ({
      ...prev,
      [level]: option.label
    }));
    
    // Close dropdown
    setFocusedLevel(null);
    setFilteredOptions({});
  }, [onFilterChange]);

  // Clear selection
  const handleClearLevel = useCallback((level) => {
    onFilterChange(level, null);
    
    setLocalSearchTerms(prev => ({
      ...prev,
      [level]: ''
    }));
  }, [onFilterChange]);

  // Clear search text for company/site
  const handleClearSearchText = useCallback((level) => {
    setLocalSearchTerms(prev => ({
      ...prev,
      [level]: ''
    }));
    
    // If in search mode (no dealer selected), call onCancelSearch
    if (isSearching && onCancelSearch) {
      onCancelSearch();
    }
    
    // If data exists, show all results
    if ((level === 'company' && companies.length > 0) || (level === 'site' && sites.length > 0)) {
      const filtered = filterOptions(level, '');
      setFilteredOptions(prev => ({
        ...prev,
        [level]: filtered
      }));
    }
  }, [companies, sites, filterOptions, isSearching, onCancelSearch]);

  // Update local search terms when selections change externally
  useEffect(() => {
    const newTerms = { ...localSearchTerms };
    
    // Check if all filters are null (clear all was clicked)
    const allNull = Object.values(selectedFilters).every(v => v === null);
    if (allNull) {
      // Clear all search terms
      setLocalSearchTerms({
        dealer: '',
        branch: '',
        company: '',
        site: ''
      });
      return;
    }
    
    Object.entries(selectedFilters).forEach(([level, value]) => {
      if (value) {
        const option = allOptions[level]?.find(opt => opt.value === value);
        if (option) {
          newTerms[level] = option.label;
        }
      } else if (!localSearchTerms[level]) {
        newTerms[level] = '';
      }
    });
    
    setLocalSearchTerms(newTerms);
  }, [selectedFilters, dealers, branches, companies, sites]);

  // Auto-open dropdown when data arrives for company/site after search
  useEffect(() => {
    if (focusedLevel === 'company' && companies.length > 0) {
      const filtered = filterOptions('company', localSearchTerms.company);
      setFilteredOptions(prev => ({
        ...prev,
        company: filtered
      }));
    }
  }, [companies, focusedLevel, filterOptions, localSearchTerms.company]);

  useEffect(() => {
    if (focusedLevel === 'site' && sites.length > 0) {
      const filtered = filterOptions('site', localSearchTerms.site);
      setFilteredOptions(prev => ({
        ...prev,
        site: filtered
      }));
    }
  }, [sites, focusedLevel, filterOptions, localSearchTerms.site]);

  const hasActiveFilters = Object.values(selectedFilters).some(value => value !== null);

  if (error) {
    return (
      <div className="entity-picker-container">
        <div className="mb-3 pb-2 border-bottom">
          <div className="d-flex align-items-center">
            <FontAwesomeIcon icon={faFilter} className="text-danger me-2" />
            <h6 className="mb-0 fw-bold text-danger">Entity Picker - Error</h6>
          </div>
        </div>
        <div className="alert alert-danger mb-0">
          <strong>Error loading data:</strong> {error.message || 'Unable to load entity data'}
        </div>
      </div>
    );
  }

  return (
    <div className="entity-picker-container">
      <div className="mb-3 pb-2 border-bottom">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <FontAwesomeIcon icon={faFilter} className="text-primary me-2" />
            <h6 className="mb-0 fw-bold text-dark">Entity Picker</h6>
            {loading && (
              <Spinner animation="border" size="sm" className="ms-2 text-primary" />
            )}
          </div>
          {hasActiveFilters && (
            <button
              className="btn btn-outline-danger btn-sm"
              onClick={onClearAll}
              title="Clear all selections"
            >
              <FontAwesomeIcon icon={faTimes} className="me-1" />
              Clear All
            </button>
          )}
        </div>
      </div>

      <div>
        {Object.entries(levelConfig).map(([level, config]) => {
          const isSelected = selectedFilters[level];
          const isActive = focusedLevel === level;
          const hasDropdown = isActive && filteredOptions[level]?.length > 0;
          const availableCount = totalCounts?.[level] || allOptions[level]?.length || 0;
          const hasTypedSearch = !isSelected && localSearchTerms[level] && (level === 'company' || level === 'site');
          
          // Disable dealer/branch when in search mode (waiting for company/site selection)
          const isDisabledBySearchMode = isSearching && (level === 'dealer' || level === 'branch');

          return (
            <div key={level} className="level-search-container mb-3">
              <div className="level-wrapper">
                <div className="level-content p-2">
                  <div className="d-flex align-items-center mb-2">
                    <FontAwesomeIcon
                      icon={config.icon}
                      className={`text-${config.color} me-2`}
                      size="sm"
                    />
                    <span className="fw-semibold text-dark me-2">{config.label}</span>
                    <Badge bg={availableCount > 0 ? config.color : 'secondary'} className="small">
                      {availableCount}
                    </Badge>
                    {isSelected && (
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className={`text-${config.color} ms-2`}
                        title="Selected"
                      />
                    )}
                  </div>

                  <div className="position-relative">
                    <InputGroup size="sm">
                      <InputGroup.Text
                        className={`bg-${config.color} bg-opacity-10 ${(level === 'company' || level === 'site') && !isSelected ? 'cursor-pointer' : ''}`}
                        onClick={() => {
                          if ((level === 'company' || level === 'site') && !isSelected) {
                            handleSearchTrigger(level);
                          }
                        }}
                        style={(level === 'company' || level === 'site') && !isSelected ? { cursor: 'pointer' } : {}}
                        title={(level === 'company' || level === 'site') && !isSelected ? `Click to search ${config.label}` : ''}
                      >
                        <FontAwesomeIcon icon={faSearch} className={`text-${config.color}`} />
                      </InputGroup.Text>
                      <Form.Control
                        ref={searchRefs[level]}
                        type="text"
                        placeholder={config.placeholder}
                        value={localSearchTerms[level]}
                        onChange={(e) => handleInputChange(level, e.target.value)}
                        onFocus={() => handleFocus(level)}
                        onBlur={() => handleBlur(level)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && (level === 'company' || level === 'site')) {
                            e.preventDefault();
                            handleSearchTrigger(level);
                          }
                        }}
                        className={isSelected ? `border-${config.color}` : ''}
                        disabled={loading && !isSelected || isDisabledBySearchMode}
                      />
                      {isSelected ? (
                        <button
                          className={`btn btn-outline-${config.color}`}
                          type="button"
                          onClick={() => handleClearLevel(level)}
                          title={`Clear ${config.label} selection`}
                        >
                          <FontAwesomeIcon icon={faTimes} size="sm" />
                        </button>
                      ) : hasTypedSearch ? (
                        <button
                          className="btn btn-outline-secondary"
                          type="button"
                          onClick={() => handleClearSearchText(level)}
                          title="Clear search text"
                        >
                          <FontAwesomeIcon icon={faTimes} size="sm" />
                        </button>
                      ) : null}
                    </InputGroup>

                    {/* Dropdown Options */}
                    {hasDropdown && (
                      <div className="dropdown-options position-absolute w-100 mt-1" style={{ zIndex: 1050 }}>
                        <Card className="shadow border-0">
                          <ListGroup variant="flush" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            {filteredOptions[level]?.map(option => (
                              <ListGroup.Item
                                key={option.value}
                                action
                                className="d-flex align-items-center py-2 cursor-pointer"
                                onClick={() => handleOptionSelect(level, option)}
                                onMouseDown={(e) => e.preventDefault()}
                                style={{ cursor: 'pointer' }}
                              >
                                <FontAwesomeIcon
                                  icon={selectedFilters[level] === option.value ? faCheckCircle : config.icon}
                                  className={`text-${config.color} me-2`}
                                  size="sm"
                                />
                                <span className="flex-grow-1">{option.label}</span>
                                {selectedFilters[level] === option.value && (
                                  <Badge bg={config.color} size="sm">Selected</Badge>
                                )}
                              </ListGroup.Item>
                            ))}
                            {filteredOptions[level]?.length === 100 && (
                              <ListGroup.Item className="text-center p-2 border-0 bg-light">
                                <small className="text-muted">
                                  Showing first 100 results. Type to refine search.
                                </small>
                              </ListGroup.Item>
                            )}
                          </ListGroup>
                        </Card>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add Selection Button */}
      {onAddSelection && (
        <div className="mt-3 mb-3">
          <button
            className="btn btn-primary btn-sm w-100"
            onClick={onAddSelection}
            disabled={
              loading ||
              (!selectedFilters.dealer && !selectedFilters.branch && !selectedFilters.company && !selectedFilters.site) ||
              accumulatedSelections.some(s => {
                // Check if this exact selection already exists
                if (selectedFilters.site && s.level === 'site' && s.id === selectedFilters.site) return true;
                if (selectedFilters.company && s.level === 'company' && s.id === selectedFilters.company) return true;
                if (selectedFilters.branch && s.level === 'branch' && s.id === selectedFilters.branch) return true;
                if (selectedFilters.dealer && s.level === 'dealer' && s.id === selectedFilters.dealer) return true;
                return false;
              })
            }
            title={
              (!selectedFilters.dealer && !selectedFilters.branch && !selectedFilters.company && !selectedFilters.site)
                ? "Select at least one level to add"
                : accumulatedSelections.some(s => {
                    if (selectedFilters.site && s.level === 'site' && s.id === selectedFilters.site) return true;
                    if (selectedFilters.company && s.level === 'company' && s.id === selectedFilters.company) return true;
                    if (selectedFilters.branch && s.level === 'branch' && s.id === selectedFilters.branch) return true;
                    if (selectedFilters.dealer && s.level === 'dealer' && s.id === selectedFilters.dealer) return true;
                    return false;
                  })
                  ? "Selection already added"
                  : "Add current selection to filter"
            }
          >
            <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
            Add Selection
          </button>
        </div>
      )}

      {/* Accumulated Selections Display */}
      {accumulatedSelections && accumulatedSelections.length > 0 && (
        <div className="mt-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="fw-semibold text-dark">
              Selected Filters ({accumulatedSelections.length})
            </span>
            {onClearAllSelections && (
              <button
                className="btn btn-outline-danger btn-sm"
                onClick={onClearAllSelections}
                title="Clear all selections"
              >
                <FontAwesomeIcon icon={faTimes} className="me-1" />
                Clear All
              </button>
            )}
          </div>
          <div className="selected-items-list" style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {accumulatedSelections.map((selection, index) => {
              const config = levelConfig[selection.level];
              return (
                <div
                  key={`${selection.level}-${selection.id}-${index}`}
                  className="d-flex justify-content-between align-items-center p-2 mb-2 bg-light rounded border"
                >
                  <div className="d-flex align-items-center flex-grow-1">
                    <FontAwesomeIcon icon={config.icon} className={`text-${config.color} me-2`} size="sm" />
                    <div className="d-flex flex-column">
                      <span className="small text-dark">{selection.label}</span>
                      <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                        {config.label} • {selection.siteCount} site{selection.siteCount !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                  {onRemoveSelection && (
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => onRemoveSelection(selection.level, selection.id)}
                      title="Remove this selection"
                      style={{ padding: '0.1rem 0.4rem' }}
                    >
                      <FontAwesomeIcon icon={faTimes} size="sm" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default EntityPicker;
